import React, { useState, useEffect } from 'react';
import { 
  RefreshCw,
  Download,
  Activity,
  Wifi,
  Server,
  AlertTriangle,
  Search,
  Grid3x3,
  List
} from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import DeviceCard from '../components/DeviceCard';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface SensorValue {
  value: number;
  unit: string;
  timestamp?: string;
  status?: string;
  quality?: number | string;
}

interface DeviceData {
  deviceId: string;
  timestamp: string;
  sensors: Record<string, SensorValue>;
  system?: {
    wifi_signal?: number;
    uptime?: number;
  };
  status?: string;
}

const ScalableDashboard: React.FC = () => {
  const [devices, setDevices] = useState<Map<string, DeviceData>>(new Map());
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  useEffect(() => {
    // Initialize WebSocket connection
    const socketIo = io(process.env.REACT_APP_WS_URL || 'http://localhost:5000', {
      transports: ['websocket', 'polling']
    });

    socketIo.on('connect', () => {
      console.log('WebSocket connected');
      setConnectionStatus('connected');
      socketIo.emit('subscribe', ['sensors']);
      toast.success('Connexion établie');
    });

    socketIo.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnectionStatus('disconnected');
      toast.error('Connexion perdue');
    });

    socketIo.on('sensorUpdate', (data: DeviceData) => {
      console.log('Sensor update received:', data);
      setDevices(prev => {
        const updated = new Map(prev);
        updated.set(data.deviceId, data);
        return updated;
      });
      setLastUpdate(new Date());
    });

    socketIo.on('heartbeat', (data: { deviceId: string; status: string; timestamp: string }) => {
      console.log('Heartbeat received:', data);
      setDevices(prev => {
        const updated = new Map(prev);
        const existing = updated.get(data.deviceId);
        if (existing) {
          updated.set(data.deviceId, {
            ...existing,
            status: data.status,
            timestamp: data.timestamp
          });
        } else {
          updated.set(data.deviceId, {
            deviceId: data.deviceId,
            timestamp: data.timestamp,
            sensors: {},
            status: data.status
          });
        }
        return updated;
      });
    });

    socketIo.on('initial-data', (data: any) => {
      console.log('Initial data received:', data);
      if (data.sensors && Array.isArray(data.sensors)) {
        const devicesMap = new Map<string, DeviceData>();
        data.sensors.forEach((sensor: DeviceData) => {
          devicesMap.set(sensor.deviceId, sensor);
        });
        setDevices(devicesMap);
      }
    });

    setSocket(socketIo);

    // Load initial data
    fetchInitialData();

    return () => {
      socketIo.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLatestData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchInitialData = async () => {
    try {
      await fetchLatestData();
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sensors/latest`);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        const devicesMap = new Map<string, DeviceData>();
        data.forEach((sensor: DeviceData) => {
          devicesMap.set(sensor.deviceId, sensor);
        });
        setDevices(devicesMap);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching latest data:', error);
    }
  };

  const exportData = async (exportFormat: 'csv' | 'json') => {
    try {
      const deviceIds = Array.from(devices.keys());
      if (deviceIds.length === 0) {
        toast.error('Aucun appareil à exporter');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sensors/export?format=${exportFormat}&deviceId=${deviceIds[0]}`
      );
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donnees-capteurs-${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`;
      a.click();
      
      toast.success('Export réussi');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const filteredDevices = Array.from(devices.values()).filter(device =>
    device.deviceId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSystemStats = () => {
    const devicesArray = Array.from(devices.values());
    let online = 0;
    let sleeping = 0;
    let offline = 0;
    
    devicesArray.forEach(d => {
      const minutesAgo = (Date.now() - new Date(d.timestamp).getTime()) / 60000;
      if (d.status === 'online' || minutesAgo < 1) {
        online++;
      } else if (minutesAgo < 20) {
        sleeping++;
      } else {
        offline++;
      }
    });
    
    const totalSensors = devicesArray.reduce((sum, device) => 
      sum + Object.keys(device.sensors).length, 0
    );

    const avgWifi = devicesArray
      .filter(d => d.system?.wifi_signal)
      .reduce((sum, d) => sum + (d.system?.wifi_signal || 0), 0) / 
      (devicesArray.filter(d => d.system?.wifi_signal).length || 1);

    return {
      total: devices.size,
      online,
      sleeping,
      offline,
      totalSensors,
      avgWifi: avgWifi.toFixed(0)
    };
  };

  const stats = getSystemStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des appareils...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Tableau de Bord IoT Agricole
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <div className="flex items-center space-x-2">
                <div className={`h-2 w-2 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                  connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  'bg-red-500'
                }`} />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {connectionStatus === 'connected' ? 'Connecté au broker MQTT' :
                   connectionStatus === 'connecting' ? 'Connexion...' : 'Déconnecté'}
                </span>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {stats.total} appareil{stats.total > 1 ? 's' : ''} • 
                {stats.online > 0 && <span className="text-green-600 dark:text-green-400">{stats.online} en ligne</span>}
                {stats.sleeping > 0 && <span className="text-blue-600 dark:text-blue-400"> • {stats.sleeping} en veille</span>}
                {stats.offline > 0 && <span className="text-red-600 dark:text-red-400"> • {stats.offline} hors ligne</span>}
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm ${
                autoRefresh 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-400'
              }`}
            >
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              <span>{autoRefresh ? 'Auto' : 'Manuel'}</span>
            </button>
            
            <button
              onClick={fetchInitialData}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Actualiser</span>
            </button>
            
            <button
              onClick={() => exportData('csv')}
              className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Server className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Appareils</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Activity className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">En Ligne</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.online}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Grid3x3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Capteurs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSensors}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Wifi className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">WiFi Moy.</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avgWifi} dBm</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and View Mode */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un appareil..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            <Grid3x3 className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Devices Grid/List */}
      {filteredDevices.length > 0 ? (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 lg:grid-cols-2 gap-6'
          : 'space-y-6'
        }>
          {filteredDevices.map(device => (
            <DeviceCard key={device.deviceId} device={device} />
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <AlertTriangle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Aucun appareil détecté
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Vérifiez que les capteurs sont connectés au broker MQTT et transmettent des données.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScalableDashboard;
